import { Text } from '@qwickapps/react-framework';
import AgentStatusCard from './AgentStatusCard';
import { Agent, useAgents } from '../../hooks/useAgents';

// Re-export shared types and mapper so existing imports from AgentList continue to work.
export type { ApiAgent, Agent } from '../../hooks/useAgents';
export { mapApiAgentToAgent } from '../../hooks/useAgents';

interface AgentListProps {
  agents?: Agent[];
  onAgentClick?: (agent: Agent) => void;
}

export default function AgentList({ agents: agentsProp, onAgentClick }: AgentListProps) {
  // agentsProp must be a stable reference (stored in state, not an inline array literal)
  // to avoid this skip flag changing on every render and re-triggering the effect.
  const { agents: hookAgents, loading, error } = useAgents(agentsProp !== undefined);

  const agents = agentsProp ?? hookAgents ?? [];

  if (loading) {
    return (
      <div style={{ padding: 16 }}>
        <Text variant="body2" content="Loading agents..." />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 16 }}>
        <Text variant="body2" content={`Error: ${error}`} customColor="#f44336" />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
        <span
          data-testid="chip"
          style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: 12,
            fontSize: 12,
            border: '1px solid var(--theme-border-color)',
            color: 'var(--theme-text-secondary)',
          }}
        >
          Agent Definitions
        </span>
        <Text variant="caption" content={`${agents.length} agents`} customColor="var(--theme-text-secondary)" />
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {agents.map((agent) => (
          <div
            key={agent.id}
            onClick={() => onAgentClick?.(agent)}
            style={{ cursor: onAgentClick ? 'pointer' : 'default' }}
          >
            <AgentStatusCard
              agent={{
                id: agent.agentId,
                name: agent.name,
                status: agent.status,
                currentTask: agent.capabilities?.join(', '),
                lastSeen: agent.lastSeen,
              }}
            />
          </div>
        ))}
      </div>

      {agents.length === 0 && (
        <Text variant="body2" content="No agents found" customColor="var(--theme-text-secondary)" />
      )}
    </div>
  );
}
